import { useCallback, useMemo } from "react";
import { useAuth } from "@jana-technology/platform-ui";
import useSWR from "swr";

import { CONFIGURATION_BASE_PATH } from "constants/configuration";
import {
  configurationFetcher,
  generateConfigurationSwrConfig,
} from "contexts/configuration/configuration-swr";
import type {
  GetAssetCategoryLookupDto,
  GetPublishedUnitConfigurationFieldDto,
  GetRiskUnitLookupDto,
} from "contexts/configuration";

import { AppliedConfigContext } from "./context";
import type { AppliedConfigContextValue } from "./types";

const API_BASE_URL = import.meta.env.API_BASE_URL as string;

export const AppliedConfigProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { getTokenSilently } = useAuth();

  const fetchConfigurationWithToken = useCallback(
    async <T,>(requestKey: ReadonlyArray<string>): Promise<T> => {
      const token = await getTokenSilently();

      return configurationFetcher<T>(requestKey, token);
    },
    [getTokenSilently],
  );
  const configurationSwrConfig = useMemo(
    () =>
      generateConfigurationSwrConfig({
        tags: {
          component: "AppliedConfigProvider",
          caller: "fetchConfigurationWithToken",
        },
      }),
    [],
  );

  const publishedAttributesResponse = useSWR<Array<GetAssetCategoryLookupDto>>(
    [`${API_BASE_URL}${CONFIGURATION_BASE_PATH}`, "attributes", "published"],
    fetchConfigurationWithToken,
    configurationSwrConfig,
  );

  const publishedUnitsResponse = useSWR<
    Array<GetPublishedUnitConfigurationFieldDto>
  >(
    [`${API_BASE_URL}${CONFIGURATION_BASE_PATH}`, "units", "published"],
    fetchConfigurationWithToken,
    configurationSwrConfig,
  );

  const riskUnitsResponse = useSWR<Array<GetRiskUnitLookupDto>>(
    [`${API_BASE_URL}${CONFIGURATION_BASE_PATH}`, "units/risks"],
    fetchConfigurationWithToken,
    configurationSwrConfig,
  );

  const value = useMemo<AppliedConfigContextValue>(
    () => ({
      publishedAttributesResponse,
      publishedUnitsResponse,
      riskUnitsResponse,
    }),
    [publishedAttributesResponse, publishedUnitsResponse, riskUnitsResponse],
  );

  return (
    <AppliedConfigContext.Provider value={value}>
      {children}
    </AppliedConfigContext.Provider>
  );
};
